import React, { useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Phone, Search, Shield, BadgeCheck, Heart, Building } from 'lucide-react';
import { InventoryLayout } from '../../components/inventory/InventoryLayout';
import { inventoryCategories } from '../../data/inventory';
import { products } from '../../data/products';
import { ProductCard } from '../../components/inventory/ProductCard';
import { OrderingProcess } from '../../components/inventory/OrderingProcess';
import { SupplierCallout } from '../../components/inventory/SupplierCallout';
export function InventoryHomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Select one product from each category for the featured section
  const featuredProducts = Object.values(products.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = p;
    return acc;
  }, {} as Record<string, (typeof products)[0]>)).slice(0, 6);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/inventory?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  return <InventoryLayout data-id="element-451">
      {/* Enhanced Hero */}
      <div className="bg-navy-900 py-14 sm:py-20 border-b border-white/10" data-id="element-452">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-453">
          <div className="max-w-3xl" data-id="element-454">
            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-455">
              Operational Equipment Catalogue
            </h1>
            <p className="text-base sm:text-lg text-steel-300 mb-8 leading-relaxed" data-id="element-456">
              Browse, compare, and request orders across our full inventory of
              professional-grade equipment.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-8 text-sm font-bold uppercase tracking-wider text-steel-400" data-id="element-457">
              <span className="flex items-center gap-2" data-id="element-458">
                <span className="w-1.5 h-1.5 rounded-full bg-cg-orange-600" data-id="element-459"></span>
                {products.length}+ Products
              </span>
              <span className="flex items-center gap-2" data-id="element-460">
                <span className="w-1.5 h-1.5 rounded-full bg-cg-orange-600" data-id="element-461"></span>
                {inventoryCategories.length} Categories
              </span>
              <span className="flex items-center gap-2" data-id="element-462">
                <span className="w-1.5 h-1.5 rounded-full bg-cg-orange-600" data-id="element-463"></span>
                4 Sectors Served
              </span>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative max-w-xl" data-id="element-464">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-465">
                <Search className="h-5 w-5 text-steel-400" data-id="element-466" />
              </div>
              <input type="text" className="block w-full pl-11 pr-4 py-4 bg-white border-0 rounded-sm text-navy-900 placeholder-steel-400 focus:ring-2 focus:ring-cg-orange-600 sm:text-base shadow-sm" placeholder="Search products, categories, or specifications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-467" />
              <button type="submit" className="absolute inset-y-1.5 right-1.5 px-4 bg-navy-900 hover:bg-navy-800 text-white text-sm font-bold uppercase tracking-wider rounded-sm transition-colors" data-id="element-468">
                Search
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Featured Products — elevated presentation */}
      <div className="bg-white py-14 sm:py-20" data-id="element-469">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-470">
          <div className="flex items-end justify-between mb-8 sm:mb-10" data-id="element-471">
            <div className="border-l-2 border-cg-orange-600 pl-4" data-id="element-472">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-cg-orange-600 block mb-1" data-id="element-473">
                Featured Equipment
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-navy-900" data-id="element-474">
                Popular Items
              </h2>
            </div>
            <Link to="/inventory" className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy-900 hover:text-cg-orange-600 transition-colors group" data-id="element-475">
              View All
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-476" />
            </Link>
          </div>

          {/* Desktop grid */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-id="element-477">
            {featuredProducts.map(product => <ProductCard key={product.slug} product={product} data-id="element-478" />)}
          </div>

          {/* Mobile horizontal scroll */}
          <div className="sm:hidden -mx-4 px-4" data-id="element-479">
            <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4" data-id="element-480">
              {featuredProducts.map(product => <div key={product.slug} className="flex-shrink-0 w-[72%] snap-start" data-id="element-481">
                  <ProductCard product={product} data-id="element-482" />
                </div>)}
            </div>
          </div>

          <div className="mt-8 sm:hidden text-center" data-id="element-483">
            <Link to="/inventory" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-cg-orange-600 hover:text-cg-orange-700 transition-colors" data-id="element-484">
              View All Inventory
              <ArrowRight className="w-3.5 h-3.5" data-id="element-485" />
            </Link>
          </div>
        </div>
      </div>

      {/* Trust/Credentials Strip (Replaces Category Grid) */}
      <div className="bg-slate-50 py-16 sm:py-20 border-t border-steel-100" data-id="element-486">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-487">
          <div className="text-center mb-12" data-id="element-488">
            <h2 className="text-xs font-bold text-steel-500 uppercase tracking-wider mb-2" data-id="element-489">
              Sectors We Serve
            </h2>
            <p className="text-lg text-navy-900 font-medium" data-id="element-490">
              Providing professional-grade equipment across critical industries.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-491">
            {/* Defence */}
            <div className="bg-white border border-steel-200 rounded-sm p-6 text-center hover:border-cg-orange-600 transition-colors group" data-id="element-492">
              <div className="w-12 h-12 bg-slate-50 border border-steel-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cg-orange-50 group-hover:border-cg-orange-200 transition-colors" data-id="element-493">
                <Shield className="w-6 h-6 text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-494" />
              </div>
              <h3 className="text-base font-bold text-navy-900 mb-2" data-id="element-495">
                Defence
              </h3>
              <p className="text-sm text-steel-600" data-id="element-496">
                Tactical load carriage, combat clothing, and ballistic
                protection for military personnel.
              </p>
            </div>

            {/* Police */}
            <div className="bg-white border border-steel-200 rounded-sm p-6 text-center hover:border-cg-orange-600 transition-colors group" data-id="element-497">
              <div className="w-12 h-12 bg-slate-50 border border-steel-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cg-orange-50 group-hover:border-cg-orange-200 transition-colors" data-id="element-498">
                <BadgeCheck className="w-6 h-6 text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-499" />
              </div>
              <h3 className="text-base font-bold text-navy-900 mb-2" data-id="element-500">Police</h3>
              <p className="text-sm text-steel-600" data-id="element-501">
                Public order equipment, covert vests, and high-visibility
                outerwear for law enforcement.
              </p>
            </div>

            {/* Medical/NGO */}
            <div className="bg-white border border-steel-200 rounded-sm p-6 text-center hover:border-cg-orange-600 transition-colors group" data-id="element-502">
              <div className="w-12 h-12 bg-slate-50 border border-steel-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cg-orange-50 group-hover:border-cg-orange-200 transition-colors" data-id="element-503">
                <Heart className="w-6 h-6 text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-504" />
              </div>
              <h3 className="text-base font-bold text-navy-900 mb-2" data-id="element-505">
                Medical & NGO
              </h3>
              <p className="text-sm text-steel-600" data-id="element-506">
                First responder kit bags, high-visibility jackets, and durable
                field equipment.
              </p>
            </div>

            {/* Custodial/Prisons */}
            <div className="bg-white border border-steel-200 rounded-sm p-6 text-center hover:border-cg-orange-600 transition-colors group" data-id="element-507">
              <div className="w-12 h-12 bg-slate-50 border border-steel-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-cg-orange-50 group-hover:border-cg-orange-200 transition-colors" data-id="element-508">
                <Building className="w-6 h-6 text-navy-900 group-hover:text-cg-orange-600 transition-colors" data-id="element-509" />
              </div>
              <h3 className="text-base font-bold text-navy-900 mb-2" data-id="element-510">
                Custodial
              </h3>
              <p className="text-sm text-steel-600" data-id="element-511">
                Control and restraint kits, flame-resistant coveralls, and
                protective training gear.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How Ordering Works — dark two-column stripe */}
      <OrderingProcess data-id="element-512" />

      {/* Contact/Enquiry CTA Strip — refined */}
      <div className="bg-white py-12 sm:py-14 border-t border-steel-100" data-id="element-513">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-514">
          <div className="border border-steel-200 rounded-sm p-6 sm:p-8 lg:p-10" data-id="element-515">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-516">
              <div data-id="element-517">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-steel-500 block mb-2" data-id="element-518">
                  Support
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-navy-900 mb-2" data-id="element-519">
                  Need assistance with your requirements?
                </h2>
                <p className="text-sm text-steel-600 leading-relaxed max-w-lg" data-id="element-520">
                  Our team can help with product selection, bulk procurement,
                  technical queries, and sourcing equipment not listed in the
                  catalogue.
                </p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-xs text-steel-500" data-id="element-521">
                  <span className="flex items-center gap-1.5" data-id="element-522">
                    <Mail className="w-3.5 h-3.5 text-cg-orange-600" data-id="element-523" />
                    orders@chilternglobal.com
                  </span>
                  <span className="flex items-center gap-1.5" data-id="element-524">
                    <Phone className="w-3.5 h-3.5 text-cg-orange-600" data-id="element-525" />
                    +44 (0) 1234 567890
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0" data-id="element-526">
                <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-navy-900 hover:bg-navy-800 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors group" data-id="element-527">
                  Contact Support
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-528" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier Callout */}
      <SupplierCallout data-id="element-529" />
    </InventoryLayout>;
}